/**
 * The always-on atmosphere layer.
 *
 * CSS-only by design. The performance budget allows one canvas-backed field per
 * route, and on the homepage that is already spent by v3/Constellation - so
 * this composites a gradient instead of running a second rAF loop.
 *
 * Renders null entirely at QUIET rather than a paused element: an always-on
 * decoration nobody will see should not be in the DOM.
 */
import type { CSSProperties } from "react";
import { useMotionLevel } from "./MotionZone";
import { AMBIENT_AMPLITUDE } from "./tokens";
import { cn } from "@/utils";

type Variant = "wash" | "sweep";

export function AmbientField({
  variant = "wash",
  className,
}: {
  variant?: Variant;
  className?: string;
}) {
  const level = useMotionLevel();
  const amp = AMBIENT_AMPLITUDE[level];

  if (amp === 0) return null;

  const animation =
    variant === "wash"
      ? "a2m-ambient-wash 14s ease-in-out infinite"
      : "a2m-ambient-sweep 9s linear infinite";

  const background =
    variant === "wash"
      ? "radial-gradient(60% 50% at 50% 40%, rgba(18,190,203,0.18), transparent 70%)"
      : "linear-gradient(90deg, transparent, rgba(18,190,203,0.14), transparent)";

  return (
    <div
      aria-hidden="true"
      className={cn("pointer-events-none absolute inset-0 overflow-hidden", className)}
      style={
        {
          background,
          animation,
          willChange: "transform, opacity",
          "--ambient-amp": String(amp),
        } as CSSProperties
      }
    />
  );
}
